import {
  IBaseEmbeddedExhibitorChat,
  NotificationType,
} from "@evennt-tech/api-v2-core";
import { create } from "zustand";
import { devtools } from "zustand/middleware";
import { apiV2 } from "api/firebase";
import { getOptimizedSelector } from "store/utils/get-optimized-selector.utils";
import { registerStore } from "store/utils/reset-store.utils";

type State = {
  isTestMode: boolean;
  embeddedChat: IBaseEmbeddedExhibitorChat | null;
  isLoading: boolean;
};

type Action = {
  enterTestMode: (chat: IBaseEmbeddedExhibitorChat) => void;
  exitTestMode: (chat: IBaseEmbeddedExhibitorChat | null) => void;
  fetch: (showId: string, force?: boolean) => Promise<void>;
  autoCatch: (showId: string) => () => void;
  reset: () => void;
};

type Store = State & Action;

const DEFAULT_STATE: State = {
  isTestMode: false,
  isLoading: false,
  embeddedChat: null,
};

export const embeddedChatStore = create<Store>()(
  devtools(
    (set, get) => ({
      ...DEFAULT_STATE,
      enterTestMode: (chat) => {
        set({ isTestMode: true, embeddedChat: chat });
      },
      exitTestMode: (chat) => {
        set({ isTestMode: false, embeddedChat: chat });
      },
      fetch: async (showId, force = false) => {
        const { isLoading, embeddedChat } = get();
        if (!force) {
          if (embeddedChat || isLoading) return;
        }

        set({ isLoading: true });

        try {
          const embeddedChat = await apiV2.embeddedExhibitorChat.getOne({
            showId,
          });
          set({ embeddedChat, isLoading: false });
        } catch (e) {
          console.error("Error occurred fetching embedded exhibitor chat: ", e);
          set({ isLoading: false, embeddedChat: null });
        }
      },
      autoCatch: (showId) => {
        const { fetch } = get();
        apiV2.notification.subscribe({
          key: NotificationType.UPDATED_EMBEDDED_EXHIBITOR_CHAT,
          showId,
          callback: async () => {
            await fetch(showId, true);
          },
        });
        return () => {
          apiV2.notification.unsubscribe({
            key: NotificationType.UPDATED_EMBEDDED_EXHIBITOR_CHAT,
          });
        };
      },
      reset: () => set({ ...DEFAULT_STATE }),
    }),

    {
      name: "embedded-chat",
    }
  )
);

export const useEmbeddedChatStore = getOptimizedSelector<Store>(
  embeddedChatStore
);

registerStore(embeddedChatStore);
